import { JobType, useAIEnhancementProcess, useAIEnhancementResult, useJobPoll } from 'dataQueries'

import { JobStatus } from 'constants/API'
import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { PhotoStudioGenerationStepId } from '../generationSteps'

interface UseAIEnhancementProcessorProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Reusable AI Enhancement processor hook
 * Handles the complete flow from processing to result display
 */
export const useAIEnhancementProcessor = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseAIEnhancementProcessorProps) => {
  const enhancementProcess = useAIEnhancementProcess()
  const enhancementJobPoll = useJobPoll(JobType.AI_ENHANCEMENT)
  const enhancementResult = useAIEnhancementResult()
  const { t } = useTranslation(['photo_studio_editing'])

  const processEnhancement = useCallback(async (
    skyReplacement?: SkyReplacementOption | null
  ) => {
    let lastJobStatus: string | undefined
    let lastJobError: string | undefined
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Step 1: Start AI Enhancement processing
      const currentPreview = getLatestPreviewFilename()
      const inputVisualFilename = currentPreview || sourceVisualFilename

      const processResponse = await enhancementProcess.mutateAsync({
        assignmentId,
        visualFilename: inputVisualFilename,
        ...(skyReplacement && { skyReplacement }),
      })

      onProgressUpdate?.(PhotoStudioGenerationStepId.UPLOAD_MASK)

      // Step 2: Poll job until completion
      const jobResult = await enhancementJobPoll.mutateAsync({
        jobId: processResponse.id
      })
      lastJobStatus = jobResult.status
      lastJobError = jobResult.error

      if (jobResult.status === JobStatus.FINISHED) {
        onProgressUpdate?.(PhotoStudioGenerationStepId.PROCESSING_COMPLETE)

        // Step 3: Get the AI result
        const aiResult = await enhancementResult.refetchWithParams(assignmentId, processResponse.id)

        if (aiResult.signedURL) {
          onComplete?.(aiResult.signedURL)
          return {
            success: true,
            resultUrl: aiResult.signedURL,
            jobId: processResponse.id
          }
        } else {
          logger.error('AI Enhancement: Job finished but no result URL', undefined, {
            context: {
              component: 'PhotoStudio',
              action: 'aiEnhancementResult',
              metadata: {
                assignmentId,
                jobId: processResponse.id,
                aiResult,
              }
            }
          })
          throw new Error('No AI result available')
        }
      } else if (jobResult.status === JobStatus.FAILED) {
        throw new Error(t('virtual_staging_error'))
      } else {
        throw new Error(`AI Enhancement job ended with unexpected status: ${jobResult.status}`)
      }
    } catch (error) {
      logger.error('AI Enhancement: Processing failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'aiEnhancementProcess',
          metadata: {
            assignmentId,
            sourceVisualFilename,
            jobStatus: lastJobStatus,
            jobError: lastJobError,
          }
        }
      })
      onError?.(error)

      return {
        success: false,
        error
      }
    }
  }, [
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    enhancementProcess,
    enhancementJobPoll,
    enhancementResult,
    onProgressUpdate,
    onComplete,
    onError,
    t
  ])

  return {
    processEnhancement,
    isProcessing: enhancementProcess.isPending || enhancementJobPoll.isPending,
    error: enhancementProcess.error || enhancementJobPoll.error
  }
}
